import { useState, useEffect, useMemo } from "react";
import {
    TextField,
    Autocomplete,
    CircularProgress,
    styled,
} from "@mui/material";
import { useDebounce } from "use-debounce";
import { fetchGooglePlaces } from "../api/GooglePlacesApi";
import { useTranslation } from "react-i18next";

interface CitiesInputProps {
    countryCode?: string;
    countryName?: string;
    value: string | null;
    errorMessage?: string;
    sx?: any;
    label?: string;
    name?: string;
    onChange: (value: string | null) => void;
}

const CityInput = ({
    countryCode,
    countryName,
    value,
    errorMessage,
    sx,
    label,
    name,
    onChange,
}: CitiesInputProps) => {
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);

    const [debouncedValue] = useDebounce(inputValue, 500);
    const { t } = useTranslation();

    useEffect(() => {
        const safeInput = debouncedValue?.trim();
        console.log(countryCode, countryName);
        if (
            !safeInput ||
            safeInput.length < 2 ||
            !countryCode ||
            !countryName
        ) {
            setOptions([]);
            return;
        }

        setLoading(true);
        fetchGooglePlaces(safeInput, countryName, countryCode)
            .then(setOptions)
            .finally(() => setLoading(false));
    }, [debouncedValue, countryName, countryCode]);

    const noOptionsText = useMemo(() => {
        if (!countryCode || !countryName)
            return t("inputPlaceholders.selectCountry");

        if (inputValue) return t("inputPlaceholders.noResultFound");

        return t("inputPlaceholders.citySearch");
    }, [countryCode, countryName, inputValue, t]);

    return (
        <Autocomplete
            value={value}
            onChange={(_, newValue) => onChange(newValue)}
            onInputChange={(_, newValue) => setInputValue(newValue)}
            inputValue={inputValue}
            options={options}
            loading={loading}
            noOptionsText={noOptionsText}
            loadingText={t("inputPlaceholders.loading")}
            renderInput={(params) => (
                <Input
                    {...params}
                    sx={sx}
                    label={label ?? t("inputLabels.city")}
                    disabled={!countryCode || !countryName}
                    error={!!errorMessage}
                    helperText={errorMessage}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? (
                                    <CircularProgress
                                        color="inherit"
                                        size={20}
                                    />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    name={name}
                />
            )}
        />
    );
};

export default CityInput;

const Input = styled(TextField)({
    backgroundColor: "white",
    borderRadius: "5px",
});
